import { BASE } from '../lib/basePath';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getNavMenu } from '../api/pages';
import NavMenu, { MobileSubMenu } from './NavMenu';
import LanguageDropdown from './LanguageDropdown';
import UserMenu from './UserMenu';
// 0.36.293 — Admin-configurable platform header (bg color, text color,
// brand text override, logo upload). All four fields default to '' or
// null, in which case the existing platform defaults render unchanged.
import { useLayoutConfig } from '../context/LayoutConfigContext';
import styles from './Header.module.css';
// 0.39.63 - Resolve =lib() page titles in nav links.
import { resolveTitleText, useResolvedTitle, useLibraryCacheRefresh } from '../lib/resolveTitleText';
import {
  brandTexts, BRAND_SIZES, BRAND_SIZE_DEFAULT, BRAND_WEIGHTS, BRAND_WEIGHT_DEFAULT,
} from '../lib/brandLines';
// 0.36.732 — Canonical Henaket Button variants; login button
// switched from the header-local .headerButton (transparent
// outlined-white) to the shared .secondary variant.
import buttonStyles from './Button.module.css';
import { colorCss } from '../lib/colorCss';
// 0.46.79 - The login button's built-in icons.
import BuiltinIcon from './BuiltinIcon';

/**
 * Two-row header following the Henaket design pattern:
 *
 *   [dark blue top row]  brand + (login | user menu)
 *   [white sub-row]      public nav links (left) + language picker (right)
 *
 * The sub-row nav is now DRIVEN BY THE STATIC PAGES CMS (since 0.33.0).
 * It fetches the list of published + show_in_nav pages from
 * /api/pages/nav and renders one <NavLink> per entry, pointing to
 * /p/:slug. Adding a new public page is a pure admin action — no code
 * change, no deploy.
 *
 * The fetch re-runs whenever the active UI language changes so titles
 * appear in the current language (with English fallback handled
 * server-side). Empty / failed nav-list responses render an empty nav
 * area silently — better than blocking page render on a nav fetch.
 *
 * 0.36.91 — Below md (768px), the inline NavLinks collapse into a
 * hamburger button on the left of the sub-row. Clicking the button
 * opens a dropdown panel anchored below the sub-row with the same
 * NavLinks stacked vertically. The language picker stays on the right
 * (not collapsed) — different cognitive category and easy reach with
 * one hand on a phone. The dropdown closes on outside-click, Escape,
 * route change, and link click.
 */
/* 0.44.23 - The navigation menu's colours as the CSS variables the
 * header's styles read (each falls back to today's colour). Tokens
 * only: anything else is ignored. */
/* 0.44.30 - Henaket text sizes offered for the menu items, each with
 * its Henaket line height. */
export const NAV_FONT_SIZES = [
  { size: 13, line: 16 }, { size: 14, line: 24 }, { size: 16, line: 24 },
  { size: 18, line: 24 }, { size: 20, line: 32 }, { size: 24, line: 32 },
];

export function navColorVars(colors) {
  const c = colors || {};
  // 0.44.31 - A Henaket token or a custom '#RRGGBB'.
  const v = (t) => colorCss(t) || undefined;
  const out = {};
  if (v(c.desktop_bg)) out['--nav-bg'] = v(c.desktop_bg);
  if (v(c.desktop_text)) out['--nav-text'] = v(c.desktop_text);
  // 0.44.27 - Each screen size has its own default (large: Text 200
  // band, small: Blue 200 band, both with dark text), set in the CSS.
  if (v(c.mobile_bg)) out['--nav-mobile-bg'] = v(c.mobile_bg);
  if (v(c.mobile_text)) out['--nav-mobile-text'] = v(c.mobile_text);
  // 0.44.28 - Stroke color per screen size (default Text 600).
  if (v(c.desktop_stroke)) out['--nav-stroke-color'] = v(c.desktop_stroke);
  if (v(c.mobile_stroke)) out['--nav-mobile-stroke-color'] = v(c.mobile_stroke);
  // 0.44.27 - The band's height (default 52px) and bottom stroke
  // (default none).
  const h = Number(c.height);
  if (Number.isInteger(h) && h >= 40 && h <= 120) out['--nav-height'] = `${h}px`;
  const w = Number(c.stroke_width);
  if (Number.isInteger(w) && w > 0 && w <= 8) out['--nav-stroke-width'] = `${w}px`;
  // 0.44.35 - Small screens have their own height and stroke width
  // (defaults 52px and none).
  const hm = Number(c.height_mobile);
  if (Number.isInteger(hm) && hm >= 40 && hm <= 120) out['--nav-mobile-height'] = `${hm}px`;
  const wm = Number(c.stroke_width_mobile);
  if (Number.isInteger(wm) && wm > 0 && wm <= 8) out['--nav-mobile-stroke-width'] = `${wm}px`;
  // 0.44.30 - The menu items' font size, a Henaket size with its line
  // height (default 16 / 24).
  const fs = NAV_FONT_SIZES.find(f => f.size === Number(c.font_size));
  if (fs) { out['--nav-font-size'] = `${fs.size}px`; out['--nav-line-height'] = `${fs.line}px`; }
  // 0.44.34 - Small screens: the mobile menu's items (default 18 / 24).
  const fm = NAV_FONT_SIZES.find(f => f.size === Number(c.font_size_mobile));
  if (fm) { out['--nav-mobile-font-size'] = `${fm.size}px`; out['--nav-mobile-line-height'] = `${fm.line}px`; }
  return out;
}

/* 0.44.40 - The header's top row (logo, brand text, actions), shared by
 * the site's Header and the admin's header preview.
 *   cfg      the header config (colors, logo, brand text, heights)
 *   loading  true while the config loads: the brand is not drawn yet
 *   preview  the brand is not a link (nothing navigates)
 *   mobile   the small-screen look at any width (admin preview)
 *   children the actions on the right */
function BrandLink({ preview, children, ...rest }) {
  if (preview) return <div {...rest}>{children}</div>;
  return <Link to="/" {...rest}>{children}</Link>;
}

/* 0.44.92 - One line of the brand text, resolved in the page's
 * language (a library value re-renders when its library arrives). */
function BrandLine({ raw, language, maxLines, size, weight, underline, className = '', measure = false }) {
  const text = useResolvedTitle(raw, language);
  if (!String(text || '').trim()) return null;
  // 0.44.98 - The line's own Henaket font size.
  // 0.44.99 - Its weight and underline too.
  const token = (BRAND_SIZES[size] || BRAND_SIZES[BRAND_SIZE_DEFAULT]).token;
  const wToken = (BRAND_WEIGHTS[weight] || BRAND_WEIGHTS[BRAND_WEIGHT_DEFAULT]).token;
  return (
    <span className={className ? `${styles.brandTitle} ${className}` : styles.brandTitle}
          data-brand-measure={measure ? '1' : undefined}
          style={{ WebkitLineClamp: maxLines, fontSize: `var(${token})`, fontWeight: `var(${wToken})`,
                   textDecoration: underline ? 'underline' : undefined }}>
      {text}
    </span>
  );
}

export function HeaderTopRow({ cfg, loading = false, preview = false, mobile = false, language = 'hy', children }) {
  const config = cfg || {};
  /* 0.46.80 - Compact: when a brand line does not fit (it is cut), the
   * row takes its small-screen look on any screen (a narrow window, a
   * long name): each line as set for small screens (its alternative
   * text, or one line cut with "..."), and the login button's icon
   * when one is set. Measured, not a breakpoint. Back to the full text only once the row is clearly
   * wider than where it switched (hysteresis, or it would flicker: the
   * shorter text fits, which would bring the long one back, which does
   * not fit...). */
  const rowRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const switchWidth = useRef(0);
  useEffect(() => {
    const row = rowRef.current;
    if (!row || mobile || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const w = row.clientWidth;
      if (compact) {
        if (w > switchWidth.current + 16) setCompact(false);
        return;
      }
      const cut = Array.from(row.querySelectorAll('[data-brand-measure]'))
        .some(el => el.offsetParent !== null && el.scrollHeight > el.clientHeight + 1);
      if (cut) { switchWidth.current = w; setCompact(true); }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [compact, mobile, cfg, language, loading]);
  const rowStyle = (() => {
    const s = {};
    if (colorCss(config.bg_color))   s.background = colorCss(config.bg_color);
    if (colorCss(config.text_color)) s.color = colorCss(config.text_color);
    const hh = Number(config.header_height) || 0;
    if (hh > 0) s.minHeight = `${hh}px`;
    // 0.46.78 - The first visit in a browser (nothing cached yet): the
    // row keeps its place but is not painted until the admin's colors
    // are known, so it appears once in the right color instead of
    // flashing the default first.
    if (loading && !cfg && !preview) s.visibility = 'hidden';
    return Object.keys(s).length ? s : undefined;
  })();
  return (
    <div ref={rowRef}
         className={[styles.topRow, mobile ? styles.topRowMobile : '', compact ? styles.topRowCompact : ''].filter(Boolean).join(' ')}
         style={rowStyle}>
      <div className={styles.container}>
          {/* 0.36.727 — Configurable gap after logo + optional
              vertical separator between logo and brand text.
              - logoGap (12/16/24) becomes the flex `gap` on .brand
                so it sits symmetrically on BOTH sides of the
                separator (Layout A).
              - Separator is a 1px vertical rule matching the logo's
                height. Color = currentColor so it inherits from the
                header's text color (which is itself admin-configurable
                via text_color); no separate color field needed.
              - Separator is suppressed when there's no logo — there
                would be nothing to separate FROM. Also suppressed
                when admin turns off `show_logo_separator`. */}
          {(() => {
            // 0.40.08 — Render nothing until the layout config has
            // arrived. Before this the block painted with an empty
            // cfg on first paint, which is what produced the
            // flash of the seeded CAF brand name. Same guard the
            // login button uses (0.39.60) and the footer (0.39.05).
            // 0.45.40 - An empty slot (not null) while loading and when
            // there is no brand, so the actions stay on the right.
            if (loading) return <div className={styles.brandSlot} />;
            const cfg = config;
            const hasLogo = !!cfg.logo_data_url;
            const rawGap = Number(cfg.logo_gap);
            const logoGap = [12, 16, 24].includes(rawGap) ? rawGap : 12;
            const logoH = Number(cfg.logo_height) || 57;
            const showSeparator = hasLogo
              && cfg.show_logo_separator !== false;
            // Nothing configured at all: no logo, no brand text. Skip
            // the whole link rather than rendering an empty clickable
            // box in the header.
            // 0.44.92 - The brand text: up to two lines, per language.
            const brandLines = brandTexts(cfg, language);
            if (!hasLogo && brandLines.length === 0) return <div className={styles.brandSlot} />;
            // 0.45.40 - The link is only as wide as the logo and text
            // (plus 16px). The slot takes the row's free width, so a
            // click on the header's empty area no longer goes home.
            return (
              <div className={styles.brandSlot}>
              <BrandLink preview={preview}
                    className={styles.brand}
                    style={{ gap: `${logoGap}px` }}>
                <div className={styles.logoBox}>
                  {/* 0.36.295 — Logo precedence is just admin-uploaded
                      data URL now. The old `getAssetUrl('header-logo')`
                      fallback was removed when the Assets feature went
                      away. Render the img only when the admin has
                      uploaded a logo via /admin/layout/header.
                      0.36.724 — Admin-configurable logo height. Falls
                      through to the CSS default (57px, ship 0.36.723)
                      when 0 / unset. */}
                  {hasLogo ? (
                    <img
                      src={cfg.logo_data_url}
                      alt=""
                      className={styles.logo}
                      style={(() => {
                        const lh = Number(cfg.logo_height) || 0;
                        return lh > 0 ? { height: `${lh}px` } : undefined;
                      })()}
                    />
                  ) : null}
                </div>
                {showSeparator && (
                  <div
                    aria-hidden="true"
                    className={styles.logoSeparator}
                    style={{
                      width: 1,
                      /* 0.44.40 - As tall as the logo: its set height, or
                         the logo's CSS default (57px, 32px on phones). */
                      height: Number(cfg.logo_height) > 0 ? `${logoH}px` : undefined,
                      backgroundColor: 'currentColor',
                      flexShrink: 0,
                      alignSelf: 'center',
                    }}
                  />
                )}
                <div className={styles.brandText}
                     style={(() => {
                       // 0.44.96 - The gap between the two lines (px),
                       // set in Pages and forms > Header. Default 4.
                       const g = Number(cfg.brand_line_gap);
                       return Number.isFinite(g) && g >= 0 && g <= 16 && cfg.brand_line_gap !== undefined
                         && cfg.brand_line_gap !== null && cfg.brand_line_gap !== ''
                         ? { gap: `${g}px` } : undefined;
                     })()}>
                  {/* 0.36.293 — Brand text override.
                      0.36.728 — Subtitle line removed per admin request.
                      0.36.741 — Title wrap-then-truncate. Computed
                      max-lines from actual header height so admins
                      who bump header_height also get more title
                      lines automatically.
                      0.40.08 — The `|| t('brand.title')` fallback is
                      GONE. That key is seeded with the CAF product
                      name, so any deployment that had not set
                      brand_text showed "CAF e-Tool" - and because
                      the layout config arrives after first paint, it
                      flashed up even on deployments that HAD set
                      their own name. The brand line now comes only
                      from brand_text; when it is unset the header
                      shows no title, which is correct for a platform
                      that is not CAF. */}
                  {(() => {
                    const headerH = Number(cfg.header_height) || 88;
                    const paddingV = 12 * 2;   // row padding top + bottom
                    // 0.44.98 - Each line has its own size, so each gets
                    // an equal share of the height and wraps to as many
                    // lines of ITS size (at its largest, x 1.2 line
                    // height) as fit in that share.
                    const share = (headerH - paddingV) / Math.max(1, brandLines.length);
                    return brandLines.map((l, i) => {
                      const px = (BRAND_SIZES[l.size] || BRAND_SIZES[BRAND_SIZE_DEFAULT]).px;
                      const maxLines = Math.max(1, Math.floor(share / (px * 1.2)));
                      const common = { size: l.size, weight: l.weight, underline: l.underline, language, maxLines };
                      // 0.46.79 - Small screens: as here (wrap), one
                      // line cut with "..." (truncate), or a text of
                      // its own (alt; none = the line is left out there).
                      if (l.mobileMode === 'truncate') {
                        return <BrandLine key={i} raw={l.raw} className={styles.brandTruncSmall} measure {...common} />;
                      }
                      if (l.mobileMode === 'alt') {
                        return (
                          <span key={i} style={{ display: 'contents' }}>
                            <BrandLine raw={l.raw} className={styles.brandLargeOnly} measure {...common} />
                            {l.mobileRaw && <BrandLine raw={l.mobileRaw} className={styles.brandSmallOnly} {...common} />}
                          </span>
                        );
                      }
                      return <BrandLine key={i} raw={l.raw} measure {...common} />;
                    });
                  })()}
                </div>
              </BrandLink>
              </div>
            );
          })()}

          <div className={styles.actions}>
            {children}
          </div>
      </div>
    </div>
  );
}

/* 0.46.79 - The login button. On small screens it may show an icon
 * instead of its text (Pages and forms > Header): a built-in one or an
 * uploaded SVG or PNG. The text stays its accessible name. Shared with
 * the admin's header preview (preview: not a link). */
export const LOGIN_ICONS = [
  { value: 'login', label: 'Log in (arrow into a door)' },
  { value: 'person', label: 'Person' },
  { value: 'user_circle', label: 'Person in a circle' },
  { value: 'upload', label: 'My own icon (SVG or PNG)' },
];

export function LoginButton({ cfg, label, preview = false }) {
  const c = cfg || {};
  const iconMode = c.login_mobile === 'icon';
  const custom = c.login_icon === 'upload' && c.login_icon_data_url;
  const builtin = LOGIN_ICONS.some(x => x.value === c.login_icon && x.value !== 'upload') ? c.login_icon : 'login';
  const cls = `${buttonStyles.button} ${buttonStyles.secondary}${iconMode ? ` ${styles.loginIconMode}` : ''}`;
  const inner = iconMode ? (
    <>
      <span className={styles.loginLargeOnly}>{label}</span>
      <span className={styles.loginSmallOnly} aria-hidden="true">
        {custom
          ? <img src={c.login_icon_data_url} alt="" className={styles.loginIconImg} />
          : <BuiltinIcon name={builtin} size={24} />}
      </span>
    </>
  ) : label;
  if (preview) return <span className={cls} style={{ pointerEvents: 'none' }} aria-label={label}>{inner}</span>;
  return <Link to="/login" className={cls} aria-label={label} title={iconMode ? label : undefined}>{inner}</Link>;
}

export default function Header() {
  // 0.45.55 - Menu labels from a library fill in when it arrives.
  useLibraryCacheRefresh();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  // 0.36.293 — Admin-configurable layout (bg, text, brand, logo).
  // `header` is null while the public fetcher is in-flight; the
  // render path tolerates null and falls back to defaults.
  const { header: headerConfig, loading: layoutLoading } = useLayoutConfig();
  const [navPages, setNavPages] = useState([]);
  // 0.44.17 - The mobile menu button's label ('' = the icon only).
  const [menuLabel, setMenuLabel] = useState('');
  // 0.44.23 - The menu's colours (Henaket tokens; '' = default).
  const [navColors, setNavColors] = useState({});
  // 0.36.91 — Translation lookup with English fallback. If the DB
  // doesn't have the new menu keys yet (first run after upgrade,
  // before `python -m app.seed` runs as part of setup.bat), `t()`
  // returns the key string itself; this wrapper substitutes a
  // sensible English default so screen readers don't announce
  // "header.menu open" and so the empty-state text reads cleanly.
  const tf = (key, fallback) => {
    const v = t(key);
    return (v === key) ? fallback : v;
  };
  /* 0.42.61 - The language picker gives up its name when the links
   * need the room.
   *
   * Measured, not guessed at a breakpoint: the links' own width against
   * the width the row can give them. A viewport rule cannot know that
   * Armenian labels are long or that an admin added a ninth page.
   *
   * HYSTERESIS, or it would flicker: dropping the name frees width,
   * which can make the links fit, which would put the name back, which
   * makes them not fit. So the name returns only when the links would
   * still fit WITH it - the width it costs, learned by measuring the
   * picker in both states. */
  const subNavRef = useRef(null);
  const subActionsRef = useRef(null);
  const widthWithName = useRef(0);
  const widthWithoutName = useRef(0);
  const [compactLang, setCompactLang] = useState(false);

  useEffect(() => {
    const nav = subNavRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => {
      const actions = subActionsRef.current;
      if (actions) {
        // Learn what the name costs, from whichever state we are in.
        if (compactLang) widthWithoutName.current = actions.offsetWidth;
        else widthWithName.current = actions.offsetWidth;
      }
      const kids = Array.from(nav.children);
      if (kids.length === 0) { setCompactLang(false); return; }
      const cs = window.getComputedStyle(nav);
      const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
      const needed = kids.reduce((sum, k) => sum + k.offsetWidth, 0)
        + gap * (kids.length - 1);
      const available = nav.clientWidth;
      const nameCost = Math.max(0, widthWithName.current - widthWithoutName.current);
      setCompactLang(prev => (prev
        // Back to the full picker only if the links still fit with it.
        ? !(needed <= available - nameCost)
        : needed > available));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    if (subActionsRef.current) ro.observe(subActionsRef.current);
    return () => ro.disconnect();
  }, [compactLang, navPages, language]);

  // 0.36.91 — Hamburger menu open/closed state. Only meaningful below
  // md (768px); above that the inline nav renders and the hamburger
  // is hidden by CSS regardless of this state.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    // 0.44.06 - The site's navigation menu (Pages and forms > Navigation
    // menu): pages, flows and outside links, in the admin's order.
    getNavMenu(language)
      .then((data) => {
        if (cancelled) return;
        // {items, hamburger_label}; a bare list from a server not yet updated.
        setNavPages(Array.isArray(data) ? data : ((data && data.items) || []));
        setMenuLabel((data && !Array.isArray(data) && data.hamburger_label) || '');
        setNavColors((data && !Array.isArray(data) && data.colors) || {});
      })
      .catch(() => { if (!cancelled) setNavPages([]); });
    return () => { cancelled = true; };
    // 0.49.29 - Again on signing in or out: a site that needs signing in
    // shows no menu until then.
  }, [language, user ? user.id : null]); // eslint-disable-line react-hooks/exhaustive-deps

  // 0.36.91 — Close on route change. When the user clicks a NavLink
  // inside the dropdown, react-router updates location, this effect
  // fires, menu closes. Also handles cases where navigation happens
  // through other means (back button, programmatic redirect) — keeps
  // the menu from getting "stuck open" across navigations.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // 0.36.91 — Close on outside click and Escape key. Listeners only
  // attached while the menu is open to keep idle state clean. We check
  // both menuRef (panel) AND buttonRef (hamburger button) — without
  // the button check, clicking the hamburger to close would register
  // as "outside" and immediately reopen on the next render.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        // Return focus to the button so keyboard users don't get lost.
        if (buttonRef.current) buttonRef.current.focus();
      }
    };
    // mousedown (not click) so the close fires before any link click
    // inside the panel — but since we whitelist the panel via the
    // menuRef.contains check above, link clicks still work.
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <header className={styles.header}>
      {/* 0.36.293 — top row inline styles override the CSS-module
          background / text colour when the admin set a token. Leaving
          either as '' falls through to the module CSS unchanged.
          0.36.724 — Admin-configurable header height. Falls through
          to the CSS default (--header-height, 88px) when 0 / unset. */}
      {/* 0.44.40 - The top row is HeaderTopRow (below), shared with the
          admin's header preview. The actions (login button or user
          menu) are passed in. */}
      <HeaderTopRow cfg={headerConfig} loading={layoutLoading} language={language}>
            {user ? (
              <>
                {/* 0.36.381 — CAF registration link removed with the
                  * CAF feature. Signed-in users now see just their
                  * name + user menu. */}
                {/* 0.48.50 - The name is in the user menu now. */}
                <UserMenu />
              </>
            ) : (
              /* 0.36.733 — Login button visibility is admin-toggleable.
               * When headerConfig.show_login_button === false, hide the
               * button entirely — the citizen site is then effectively
               * public (no visible login prompt). Admins still log in
               * via the dedicated gt18-admin.govtech.am subdomain.
               * Default (true or unset) preserves current behavior.
               *
               * 0.39.60 — Don't render until the layout config has
               * loaded (layoutLoading). Before, headerConfig was null
               * on first paint, so `headerConfig?.show_login_button
               * !== false` was true (undefined !== false) and the
               * button flashed on, then disappeared once the config
               * arrived with show_login_button:false. Gating on
               * !layoutLoading removes that flash. */
              /* 0.46.98 - Not on the login pages themselves (/login,
               * /login/verify, /login/id-card): it would only lead to
               * the page already open. */
              /* 0.47.96 - Not under another site's path (eipo.am/gt18):
               * the public site has no sign-in there. */
              (!layoutLoading && headerConfig?.show_login_button !== false && !BASE
                && !/^\/login(\/|$)/.test(location.pathname)) && (
                <LoginButton cfg={headerConfig} label={t('header.login')} />
              )
            )}
      </HeaderTopRow>

      <div className={styles.subRow} style={navColorVars(navColors)}>
        <div className={styles.container}>
          {/* 0.36.91 — Hamburger button. CSS hides this above 768px;
            * below it, it's shown and the inline .subRowNav is hidden.
            * `aria-expanded` and `aria-controls` give screen readers
            * the open/closed state and target panel id. */}
          <button
            ref={buttonRef}
            type="button"
            className={menuLabel ? `${styles.hamburger} ${styles.hamburgerWithLabel}` : styles.hamburger}
            aria-label={menuOpen
              ? tf('header.menu_close', 'Close menu')
              : tf('header.menu_open', 'Open menu')}
            aria-expanded={menuOpen}
            aria-controls="subrow-mobile-menu"
            onClick={() => setMenuOpen(v => !v)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 aria-hidden="true">
              {menuOpen ? (
                /* Close (X) icon when menu is open — universal "tap
                 * me to close" affordance. */
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                /* Three-line hamburger when menu is closed. */
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
            {menuLabel && (
              <span className={styles.hamburgerLabel}>{resolveTitleText(menuLabel, language)}</span>
            )}
          </button>

          {/* Sub-row left side: public top-level nav, sourced from the
           * static pages CMS. The list is dynamic — admins add/remove
           * pages via /admin/pages and the nav updates without a code
           * change.
           *
           * 0.36.91 — Hidden below 768px (replaced by the hamburger). */}
          <nav className={styles.subRowNav} ref={subNavRef}>
            {/* 0.44.11 - Both levels (the second a mega-menu panel), drawn by
                the component the admin's preview uses too. */}
            <NavMenu items={navPages} language={language}
                     linkClass={styles.subRowLink} activeClass={styles.subRowLinkActive} />
          </nav>
          <div className={styles.subRowActions} ref={subActionsRef}>
            <LanguageDropdown compact={compactLang} />
          </div>
        </div>

        {/* 0.36.91 — Mobile dropdown panel. Rendered conditionally so
          * a closed menu adds zero DOM nodes. Positioned absolutely
          * below the sub-row's bottom edge. The panel itself uses
          * NavLink so active-route state still styles correctly. */}
        {menuOpen && (
          <div
            ref={menuRef}
            id="subrow-mobile-menu"
            className={styles.mobileMenu}
            role="menu"
            aria-label={tf('header.nav_label', 'Main navigation')}
          >
            {navPages.length === 0 ? (
              <div className={styles.mobileMenuEmpty}>
                {tf('header.nav_empty', 'No pages')}
              </div>
            ) : (
              navPages.map((p) => (p.columns && p.columns.length ? (
                // 0.44.16 - The desktop panel's structure and style, opened
                // under the item by tapping it (see NavMenu).
                <MobileSubMenu key={p.id} item={p} language={language} rowClass={styles.mobileMenuLink} />
              ) : p.external ? (
                <a key={p.id} href={p.href} role="menuitem" className={styles.mobileMenuLink}
                   {...(p.new_tab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                  {resolveTitleText(p.label, language)}
                </a>
              ) : (
                <NavLink
                  key={p.id}
                  to={`/${language}/${p.href}`}
                  end={!p.href || String(p.href).startsWith('#')}
                  role="menuitem"
                  {...(p.new_tab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className={({ isActive }) =>
                    isActive
                      ? `${styles.mobileMenuLink} ${styles.mobileMenuLinkActive}`
                      : styles.mobileMenuLink
                  }
                >
                  {resolveTitleText(p.label, language)}
                </NavLink>
              )))
            )}
          </div>
        )}
      </div>
    </header>
  );
}

